import {
  ITypeFieldFilterOptions,
  OperatorType,
} from '~/client/graph/types.generated'
import { locationFilterPropMap } from '~/client/src/shared/constants/FilterPropertyMaps/LocationFilterPropMap'
import PermitField from '~/client/src/shared/models/PermitField'

export function evaluateFiltersWithLbsContext(
  filters: ITypeFieldFilterOptions[],
  item: Record<string, any>,
  isLbsEnabled: boolean,
  lbsRestrictedIds: Set<string>,
  workflowFieldsMap?: Record<string, PermitField>,
): boolean {
  if (!isLbsEnabled) {
    return evaluateFilters(filters, item, workflowFieldsMap)
  }

  const idFilters: ITypeFieldFilterOptions[] = []
  const otherFilters: ITypeFieldFilterOptions[] = []

  filters.forEach(filter => {
    if (filter.propertyPath === locationFilterPropMap.ID && !filter.fieldId) {
      idFilters.push(filter)
    } else {
      otherFilters.push(filter)
    }
  })

  let isAllowed = !lbsRestrictedIds.has(item.id)

  // Apply ID filter overrides
  idFilters.forEach(filter => {
    if (filter.operator === OperatorType.In) {
      isAllowed = isAllowed || filter.values.includes(item.id)
    } else if (filter.operator === OperatorType.NotIn) {
      isAllowed = isAllowed && !filter.values.includes(item.id)
    }
  })

  // Apply other filters normally
  return isAllowed && evaluateFilters(otherFilters, item, workflowFieldsMap)
}

/**
 * Drops ids that no longer exist from every static resource-id allow-list.
 *
 * An allow-to-select list stores record ids, and those ids outlive the records:
 * `softDeleteManyLocations` does not run the resource-control cascade that an
 * is-bookable or workflow-types edit runs, so deleting a resource leaves its id
 * behind in every field that named it. An allow-list whose ids all point at
 * deleted records matches nothing, and the picker comes up empty rather than
 * unrestricted.
 *
 * Pruning to empty is the whole fix: `evaluateFilterOption` already treats a
 * filter with no values as not applying. `NotIn` exclusions are left alone —
 * excluding an id that no longer exists is already a no-op — field-dependent
 * filters are left alone (see below) and the filter object is returned unchanged
 * when nothing was stale, so memoized callers keep their reference.
 */
export function pruneMissingIdAllowances(
  filters: ITypeFieldFilterOptions[] | null | undefined,
  existingIds: Set<string>,
): ITypeFieldFilterOptions[] {
  return (filters || []).map(filter => {
    if (
      filter.propertyPath !== locationFilterPropMap.ID ||
      filter.operator !== OperatorType.In ||
      // Static allow-lists only. A conditional rule ("when field A is X, allow
      // only resource B") carries the identical shape plus a fieldId, and
      // emptying its values would make evaluateFilterOption return true at the
      // `!values.length` guard — BEFORE it checks the dependency — so triggering
      // the rule would permit every live resource instead of enforcing it.
      filter.fieldId ||
      !filter.values?.length
    ) {
      return filter
    }

    const values = filter.values.filter(id => existingIds.has(id))
    return values.length === filter.values.length
      ? filter
      : { ...filter, values }
  })
}

export function evaluateFilters(
  filters: ITypeFieldFilterOptions[],
  item: Record<string, any>,
  workflowFieldsMap?: Record<string, PermitField>,
): boolean {
  return filters.every(filter =>
    evaluateFilterOption(filter, item, workflowFieldsMap),
  )
}

function evaluateFilterOption(
  filter: ITypeFieldFilterOptions,
  item: Record<string, any>,
  workflowFieldsMap?: Record<string, PermitField>,
): boolean {
  if (!filter.values?.length) return true

  const value = item[filter.propertyPath]

  // Check if this is a field-dependent filter
  if (filter.fieldId) {
    const dependentField = workflowFieldsMap?.[filter.fieldId]
    // If the dependent field doesn't exist, the filter doesn't apply
    if (!dependentField) return true

    // Check if the dependent field value matches any of the fieldValues.
    // Values can be plain strings, { id } (locations/equipment), or
    // { locationId } (material resources).
    const fieldValueMatches = dependentField.values.some(v => {
      const resolvedId = v?.id ?? v?.locationId
      return resolvedId
        ? filter.fieldValues.includes(resolvedId)
        : filter.fieldValues.includes(v)
    })
    // If the dependent field value doesn't match, this filter doesn't apply
    if (!fieldValueMatches) return true
  }

  // for now, we only support single value filters
  switch (filter.operator) {
    case OperatorType.Is:
      return value === filter.values[0]
    case OperatorType.In:
      return filter.values.includes(value)
    case OperatorType.NotIn:
      return !filter.values.includes(value)
    case OperatorType.Contains:
      return value.includes(filter.values[0])
    case OperatorType.DoesNotContain:
      return !value.includes(filter.values[0])
    default:
      return false
  }
}
